import { type MouseEvent as ReactMouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { User, Bot, MessageCircle, Info, UserCheck, Globe, Mail, MessageSquare, Webhook } from 'lucide-react';
import type { Conversation, MessageType } from '../lib/types';
import { identityTier, TIER_COLORS } from '../lib/identityTier';
import { sentimentMeta } from '../lib/sentiment';

// "Who answered last" indicator — customer-last is the actionable case
// (awaiting an agent reply), so it gets a warm colour; agent/AI are muted.
const LAST_SENDER: Record<MessageType, { Icon: typeof User; label: string; cls: string }> = {
  customer: { Icon: User, label: 'Customer replied last — awaiting reply', cls: 'text-amber-600' },
  agent: { Icon: MessageCircle, label: 'Agent replied last', cls: 'text-primary-600' },
  ai: { Icon: Bot, label: 'AI replied last', cls: 'text-violet-500' },
  system: { Icon: Info, label: 'System message last', cls: 'text-gray-400' },
};

// Brand glyphs (lucide has no WhatsApp/Facebook logos) — minimal inline SVGs.
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

// Per-channel icon (replaces the text channel badge — less width, instantly
// recognizable). Brand colours for WhatsApp/Facebook; muted for the rest.
function ChannelIcon({ channel }: { channel: Conversation['channel'] }) {
  const cls = 'w-3.5 h-3.5 flex-shrink-0';
  switch (channel) {
    case 'whatsapp':
      return <span title="WhatsApp" role="img" aria-label="WhatsApp"><WhatsAppIcon className={`${cls} text-[#25D366]`} /></span>;
    case 'facebook':
      return <span title="Facebook" role="img" aria-label="Facebook"><FacebookIcon className={`${cls} text-[#1877F2]`} /></span>;
    case 'email':
      return <span title="Email" role="img" aria-label="Email"><Mail className={`${cls} text-gray-500`} /></span>;
    case 'sms':
      return <span title="SMS" role="img" aria-label="SMS"><MessageSquare className={`${cls} text-gray-500`} /></span>;
    case 'api':
      return <span title="API" role="img" aria-label="API"><Webhook className={`${cls} text-gray-400`} /></span>;
    case 'web':
    default:
      return <span title="Web chat" role="img" aria-label="Web chat"><Globe className={`${cls} text-gray-400`} /></span>;
  }
}

function formatTime(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString();
}

interface InboxListProps {
  chats: Conversation[];
  selectedId?: string;
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  onContextMenu?: (e: ReactMouseEvent, chat: Conversation) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** uid → display name, so rows can show who a conversation is assigned to. */
  agentNames?: Record<string, string>;
  /** Show the bulk-select checkbox column (advanced view only). */
  showSelect?: boolean;
}

export function InboxList({
  chats,
  selectedId,
  selected,
  onToggleSelect,
  onContextMenu,
  hasMore,
  onLoadMore,
  agentNames,
  showSelect = true,
}: InboxListProps) {
  // Paging is by an explicit "Load more" button (below), not auto-scroll.
  return (
    <>
      <ul className="divide-y divide-gray-100">
        {chats.map((c) => {
          const isBulkSel = selected.has(c.id);
          const isOpen = c.id === selectedId;
          // Gmail-style unread emphasis: a conversation the agent hasn't read
          // (unreadCount > 0) renders bold. Opening it fires api.markRead
          // (ChatDetailPage), which zeroes unreadCount on the chat doc; the live
          // snapshot then drops the bold weight.
          const isUnread = (c.unreadCount ?? 0) > 0;
          return (
            <li
              key={c.id}
              data-chat-id={c.id}
              onContextMenu={(e) => onContextMenu?.(e, c)}
              className={`flex items-stretch transition-colors ${
                isOpen ? 'bg-primary-50' : isBulkSel ? 'bg-amber-50' : 'hover:bg-gray-50'
              }`}
            >
              {(showSelect || isBulkSel) && (
                <label
                  className="pl-3 pt-4 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    checked={isBulkSel}
                    onChange={() => onToggleSelect(c.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </label>
              )}
              <Link
                to={`/inbox/${c.id}`}
                className="flex-1 min-w-0 px-3 py-3 text-inherit no-underline"
              >
                <div className="flex justify-between items-center">
                  <span className="flex gap-2 items-center min-w-0">
                    {(() => {
                      const it = identityTier(c);
                      return (
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-black/5"
                          style={{ background: TIER_COLORS[it.tier] }}
                          title={`${it.label} · Priority: ${c.priority}`}
                          aria-label={`Identity: ${it.label}`}
                          role="img"
                        />
                      );
                    })()}
                    {c.lastSenderType && LAST_SENDER[c.lastSenderType] && (() => {
                      const ls = LAST_SENDER[c.lastSenderType];
                      return (
                        <span className="flex-shrink-0" title={ls.label}>
                          <ls.Icon className={`w-3.5 h-3.5 ${ls.cls}`} />
                        </span>
                      );
                    })()}
                    <span className="min-w-0 flex flex-col">
                      <span
                        className={`text-sm truncate ${
                          isUnread ? 'font-bold text-gray-900' : 'font-normal text-gray-800'
                        }`}
                      >
                        {c.contactName ||
                          c.contactEmail ||
                          c.contactPhone ||
                          (c.contactId.startsWith('anon-') ? 'Anonymous' : c.contactId.slice(0, 20))}
                      </span>
                      {(c.contactEmail || c.contactPhone) && (
                        <span className="text-[11px] text-gray-500 truncate">
                          {c.contactEmail || c.contactPhone}
                        </span>
                      )}
                    </span>
                    {c.unreadCount > 0 && (
                      <span className="bg-primary-600 text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5 flex-shrink-0">
                        {c.unreadCount}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    {/* Channel as an icon (was a text badge) + assignee + CSAT,
                        hoisted onto the title row so the meta row below can go
                        away entirely — saves a line so more chats fit. */}
                    <ChannelIcon channel={c.channel} />
                    {c.assigneeUid && (
                      <span title={`Assigned: ${agentNames?.[c.assigneeUid] ?? 'agent'}`} role="img" aria-label="Assigned to an agent">
                        <UserCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      </span>
                    )}
                    {typeof c.csatScore === 'number' && (
                      <span className="text-[10px] font-medium text-amber-600 flex-shrink-0" title={c.csatComment ? `CSAT: “${c.csatComment}”` : 'Customer satisfaction'}>
                        {c.csatScore}★
                      </span>
                    )}
                    {/* Current customer sentiment ("Now") — same face/scale as
                        the conversation header, so the whole queue's mood is
                        glanceable from the list. */}
                    {(() => {
                      const sm = sentimentMeta(c.sentimentLast);
                      return sm ? (
                        <span title={`Customer sentiment: ${sm.label}`} aria-label={`Sentiment: ${sm.label}`} role="img">
                          <sm.Icon className="w-3.5 h-3.5" style={{ color: sm.color }} />
                        </span>
                      ) : null;
                    })()}
                    <span className="text-[11px] text-gray-500">{formatTime(c.lastMessageAt)}</span>
                  </span>
                </div>
                <div
                  className={`mt-1 text-[13px] truncate ${
                    isUnread ? 'font-semibold text-gray-800' : 'font-normal text-gray-600'
                  }`}
                  dir="auto"
                >
                  {c.lastMessagePreview || <span className="italic text-gray-400">(no messages yet)</span>}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
      {hasMore && onLoadMore && (
        <div className="py-3 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Load more
          </button>
        </div>
      )}
    </>
  );
}
